import configService from 'ConfigService';

let apiHostUrl = '';

configService.promise.then(config => {
    apiHostUrl = config.apiHostUrl;
});

function getAIUrl(endpoint) {
    return apiHostUrl + '/AI/' + endpoint;
}

async function makeRequest(url, method, data) {
  return new Promise(function (resolve, reject) {
      let xhr = new XMLHttpRequest();
      xhr.open(method, url);
      xhr.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
      xhr.onload = function () {
          if (this.status >= 200 && this.status < 300) {
              resolve(JSON.parse(xhr.response));
          } else {
              reject({
                  status: this.status,
                  statusText: xhr.statusText
              });
          }
      };
      xhr.onerror = function () {
          reject({
              status: this.status,
              statusText: xhr.statusText
          });
      };
      xhr.send(JSON.stringify(data));
  });
}


/**
 * Sends a user message along with conversation history to the server and returns a response object.
 * @param {string} userMessage - The message to send to the server.
 * @param {Array<Object>} conversationHistory - The entire conversation history, starts empty for the first message.
 * @returns {Promise<{message: string, supportText: string, txtPaths: string[], newID: string, updatedMessagesUsed: number}>} - A promise that resolves to an object with message, supportText, txtPaths, newID, and updatedMessagesUsed properties.
 */
export async function handleUserMessageWithFile(file, userMessage, conversationHistory = [], userContext = '') {
  const token = sessionStorage.getItem('loginToken');
  const formData = new FormData();
  formData.append('token', token);
  formData.append('userMessage', userMessage || '');
  formData.append('userContext', userContext);
  formData.append('conversationHistory', JSON.stringify(conversationHistory));
  formData.append('file', file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', getAIUrl('HandleUserMessageWithFile'));
    // Do NOT set Content-Type — browser sets it automatically with multipart boundary
    xhr.onload = function () {
      if (this.status >= 200 && this.status < 300) {
        const response = JSON.parse(xhr.response);
        if (response.TOKEN) sessionStorage.setItem('loginToken', response.TOKEN);
        if (response.ERROR && response.ERROR.CODE !== 0) {
          reject(new Error(response.ERROR.MESSAGE));
          return;
        }
        resolve({
          message: response.RESPONSE.message,
          supportText: response.RESPONSE.supportText,
          txtPaths: response.RESPONSE.txtPaths,
          newID: response.RESPONSE.newID,
          updatedMessagesUsed: response.RESPONSE.updatedMessagesUsed,
          action: response.RESPONSE.action || null
        });
      } else if (this.status === 400) {
        const errResponse = JSON.parse(xhr.response);
        reject(new Error(errResponse.ERROR ? errResponse.ERROR.MESSAGE : xhr.statusText));
      } else {
        reject({ status: this.status, statusText: xhr.statusText });
      }
    };
    xhr.onerror = function () {
      reject({ status: this.status, statusText: xhr.statusText });
    };
    xhr.send(formData);
  });
}

export async function handleUserMessage(userMessage, conversationHistory = [], userContext = '', vectorDbConnectionId = '') {
  try {
    //Get token from sessionStorage
    const token = sessionStorage.getItem('loginToken');

    // Ensure that conversationHistory is an array
    if (!Array.isArray(conversationHistory)) {
      throw new Error('conversationHistory must be an array');
    }

    const requestBody = {
      token: token,
      userMessage: userMessage,
      conversationHistory: conversationHistory,
      userContext: userContext
    };

    if (vectorDbConnectionId) {
      requestBody.vectorDbConnectionId = vectorDbConnectionId;
    }

    const response = await makeRequest(getAIUrl('HandleUserMessage'), 'POST', requestBody);

    if (response.TOKEN) {
      if (response.TOKEN) sessionStorage.setItem('loginToken', response.TOKEN);
    }

    if (response.ERROR && response.ERROR.CODE !== 0) {
      console.error('API Error:', response.ERROR.MESSAGE);
      throw new Error(response.ERROR.MESSAGE);
    }

    return {
      message: response.RESPONSE.message,
      supportText: response.RESPONSE.supportText,
      txtPaths: response.RESPONSE.txtPaths,
      newID: response.RESPONSE.newID,
      updatedMessagesUsed: response.RESPONSE.updatedMessagesUsed,
      action: response.RESPONSE.action || null
    };
  } catch (error) {
    console.error('Error during HandleUserMessage request:', error);
    throw error;
  }
}



/**
 * Updates the AI response rating.
 * @param {number} responseId - The ID of the AI response to rate.
 * @param {boolean} responseRating - The rating to assign to the response (true or false).
 * @returns {Promise<string>} - A promise that resolves to a message indicating the result of the rating update.
 */
export async function UpdateAIResponseRating(responseId, responseRating) {
  // Extract userId and accountId from localStorage
  const profile = JSON.parse(localStorage.getItem('profile'));
  const userId = profile.UserId;
  const accountId = profile.AccountId;

  // Get token from sessionStorage
  const token = sessionStorage.getItem('loginToken');

  const requestBody = {
    "userId": userId,
    "accountId": accountId,
    "token": token,
    "responseId": responseId,
    "responseRating": responseRating
  };

  try {
    const response = await makeRequest(getAIUrl('UpdateAIResponseRating'), 'POST', requestBody);

    // Update the token in sessionStorage
    if (response.TOKEN) sessionStorage.setItem('loginToken', response.TOKEN);

    if (response.ERROR && response.ERROR.CODE !== 0) {
      console.error('API Error:', response.ERROR.MESSAGE);
      throw new Error(response.ERROR.MESSAGE);
    }

    return response.RESPONSE.RESULT.MESSAGE;
  } catch (error) {
    console.error('Error during UpdateAIResponseRating request:', error);
    return 'An error occurred while updating the AI response rating.';
  }
}

/**
 * Fetches the message limit and usage for the user's account.
 * @returns {Promise<Object>} - A promise that resolves to an object containing the message limit and messages used.
 */
export async function GetMessageLimitAndUsage() {
  // Extract accountId from localStorage
  const profile = JSON.parse(localStorage.getItem('profile'));
  const accountId = profile.AccountId;

  // Get token from sessionStorage
  const token = sessionStorage.getItem('loginToken');

  const requestBody = {
    "token": token,
    "accountId": accountId
  };

  try {
    const response = await makeRequest(getAIUrl('GetMessageLimitAndUsage'), 'POST', requestBody);

    // Update the token in sessionStorage
    if (response.TOKEN) sessionStorage.setItem('loginToken', response.TOKEN);

    if (response.ERROR && response.ERROR.CODE !== 0) {
      console.error('API Error:', response.ERROR.MESSAGE);
      throw new Error(response.ERROR.MESSAGE);
    }

    return {
      MessageLimit: response.RESPONSE.MessageLimit,
      MessagesUsed: response.RESPONSE.MessagesUsed
    };
  } catch (error) {
    console.error('Error during GetMessageLimitAndUsage request:', error);
    throw new Error('An error occurred while fetching message limit and usage.');
  }
}



/**
 * Sends a message to an AI to generate a JSON structure for a form.
 * @param {string} message - The message to send to the AI.
 * @param {Array} jsonStructure - An optional array of objects representing a conversation between a user and an AI assistant.
 * @returns {Promise<string>} - A promise that resolves to the content of the AI's response.
 */
export async function AIJsonBuilder(message, jsonStructure) {
  let messages;
  
  if (!jsonStructure) {
    messages = [
        {
        role: "system",
        content: "You are a helpful Perfectforms Assistant designed to output JSON. Create all types of objects based on the user request, and offer only one option for each needed field. Respond with a JSON structure that includes the form name and form objects, with details about each field and do not add any additional text. These are the possible objects that we can have in factoryKey:\ncontrol_bullet_text\ncontrol_button\ncontrol_captcha\ncontrol_checkbox\ncontrol_checkbox_group\ncontrol_date_calendar\ncontrol_date_input\ncontrol_drop_down\ncontrol_file_attachment\ncontrol_group\ncontrol_horizontal_separator\ncontrol_horizontal_slider\ncontrol_large_heading\ncontrol_link\ncontrol_list\ncontrol_mask_text_input\ncontrol_medium_heading\ncontrol_multiple_text_input\ncontrol_numeric_input\ncontrol_numeric_stepper\ncontrol_page_button\ncontrol_password_input\ncontrol_picture\ncontrol_print_button\ncontrol_progress_bar\ncontrol_radio_button\ncontrol_radio_button_group\ncontrol_ranking\ncontrol_small_heading\ncontrol_small_text\ncontrol_submit_button\ncontrol_table\ncontrol_text\ncontrol_text_input\ncontrol_time_input\ncontrol_timer\ncontrol_vertical_separator\ncontrol_vertical_slider\ncontrol_websignature"
        },
        {
        role: "user",
        content: "I want to make a Job Application form for Sailing Personel"
        },
        {
          "role": "assistant",
          "content": "{\n  \"FormObjects\": [\n    {\n      \"factoryKey\": \"control_large_heading\",\n      \"attributes\": {\n        \"text\": \"Sailing Personnel Job Application\",\n        \"textWeight\": \"bold\",\n        \"controlName\": \"Label_Title\",\n        \"isMandatory\": false,\n        \"obligatoryMessage\": \"\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_text\",\n      \"attributes\": {\n        \"text\": \"First Name\",\n        \"controlName\": \"Label_FirstName\",\n        \"isMandatory\": false,\n        \"obligatoryMessage\": \"\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_text_input\",\n      \"attributes\": {\n        \"text\": \"\",\n        \"controlName\": \"FullName\",\n        \"isMandatory\": true,\n        \"obligatoryMessage\": \"Please enter your full name!\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_text\",\n      \"attributes\": {\n        \"text\": \"Email Address\",\n        \"controlName\": \"Label_EmailAddress\",\n        \"isMandatory\": false,\n        \"obligatoryMessage\": \"\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_text_input\",\n      \"attributes\": {\n        \"text\": \"\",\n        \"controlName\": \"EmailAddress\",\n        \"isMandatory\": true,\n        \"obligatoryMessage\": \"Please enter your email address!\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_text\",\n      \"attributes\": {\n        \"text\": \"Phone Number\",\n        \"controlName\": \"Label_PhoneNumber\",\n        \"isMandatory\": false,\n        \"obligatoryMessage\": \"\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_mask_text_input\",\n      \"attributes\": {\n        \"text\": \"\",\n        \"controlName\": \"PhoneNumber\",\n        \"isMandatory\": true,\n        \"obligatoryMessage\": \"Please enter your phone number!\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_text\",\n      \"attributes\": {\n        \"text\": \"Position Applied For\",\n        \"controlName\": \"Label_PositionAppliedFor\",\n        \"isMandatory\": false,\n        \"obligatoryMessage\": \"\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_drop_down\",\n      \"attributes\": {\n        \"text\": \"\",\n        \"controlName\": \"PositionAppliedFor\",\n        \"isMandatory\": true,\n        \"obligatoryMessage\": \"Please select a position!\",\n        \"gridData\": [\n          {\n            \"value\": \"1\",\n            \"selected\": \"selected\",\n            \"caption\": \"Captain\"\n          },\n          {\n            \"value\": \"2\",\n            \"caption\": \"First Officer\"\n          },\n          {\n            \"value\": \"3\",\n            \"caption\": \"Deckhand\"\n          },\n          {\n            \"value\": \"4\",\n            \"caption\": \"Engineer\"\n          },\n          {\n            \"value\": \"5\",\n            \"caption\": \"Steward\"\n          }\n        ]\n      }\n    },\n    {\n      \"factoryKey\": \"control_text\",\n      \"attributes\": {\n        \"text\": \"Years of Experience\",\n        \"controlName\": \"Label_Experience\",\n        \"isMandatory\": false,\n        \"obligatoryMessage\": \"\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_numeric_input\",\n      \"attributes\": {\n        \"text\": \"\",\n        \"controlName\": \"YearsExperience\",\n        \"isMandatory\": true,\n        \"obligatoryMessage\": \"Please enter your years of experience!\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_text\",\n      \"attributes\": {\n        \"text\": \"Availability Date\",\n        \"controlName\": \"Label_AvailabilityStartDate\",\n        \"isMandatory\": false,\n        \"obligatoryMessage\": \"\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_date_input\",\n      \"attributes\": {\n        \"text\": \"\",\n        \"controlName\": \"AvailabilityStartDate\",\n        \"isMandatory\": true,\n        \"obligatoryMessage\": \"Please enter your availability date!\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_text\",\n      \"attributes\": {\n        \"text\": \"Additional Information\",\n        \"controlName\": \"Label_AdditionalInformation\",\n        \"isMandatory\": false,\n        \"obligatoryMessage\": \"\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_multiple_text_input\",\n      \"attributes\": {\n        \"text\": \"\",\n        \"controlName\": \"AdditionalInformation\",\n        \"isMandatory\": false,\n        \"obligatoryMessage\": \"\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_text\",\n      \"attributes\": {\n        \"text\": \"Upload Your Resume\",\n        \"controlName\": \"Label_Resume\",\n        \"isMandatory\": false,\n        \"obligatoryMessage\": \"\"\n      }\n    },\n    {\n      \"factoryKey\": \"control_file_attachment\",\n      \"attributes\": {\n        \"text\": \"\",\n        \"controlName\": \"Resume\",\n        \"isMandatory\": true,\n        \"obligatoryMessage\": \"Please upload your resume!\"\n      },\n    {\n      \"factoryKey\": \"control_submit_button\",\n      \"attributes\": {\n        \"text\": \"Submit your application\",\n        \"controlName\": \"Submit\",\n        \"isMandatory\": false,\n        \"obligatoryMessage\": \"\"\n      },\n    }\n  ]\n}\n"
        },
        {
        role: "user",
        content: message
        }
    ];
  } else {
    messages = jsonStructure;
  }
  
  // Get token from sessionStorage
  const token = sessionStorage.getItem('loginToken');

  const requestBody = {
    "token": token,
    "model": "gpt-3.5-turbo-16k",
    "messages": messages,
    "temperature": 0.2,
    "max_tokens": 2500
  };

  try {
    const response = await makeRequest(getAIUrl('JsonBuilder'), 'POST', requestBody);

    // Update the token in sessionStorage
    if (response.TOKEN) sessionStorage.setItem('loginToken', response.TOKEN);

    if (response.ERROR && response.ERROR.CODE !== 0) {
      console.error('API Error:', response.ERROR.MESSAGE);
      throw new Error(response.ERROR.MESSAGE);
    }


    return response.RESPONSE.choices[0].message.content;
  } catch (error) {
    console.error('Error during AIJsonBuilder request:', error);
    return 'An error occurred while fetching response from the AI.';
  }
}

/**
 * Tests an AI-created connection by calling each action's endpoint and validating/fixing receive params.
 * @param {Object} testData - The test request payload
 * @returns {Promise<{message: string, results: Array, action: Object}>}
 */
export async function testAIConnection(testData) {
  try {
    const response = await makeRequest(getAIUrl('TestConnection'), 'POST', testData);

    // TestConnection wraps response in RESULT (like Integration endpoints)
    const result = response.RESULT || response;

    if (result.TOKEN) {
      sessionStorage.setItem('loginToken', result.TOKEN);
    }

    if (result.ERROR && result.ERROR.CODE !== 0 && result.ERROR.CODE !== "0") {
      console.error('API Error:', result.ERROR.MESSAGE);
      throw new Error(result.ERROR.MESSAGE);
    }

    return result.RESPONSE;
  } catch (error) {
    console.error('Error during TestConnection:', error);
    throw error;
  }
}

/**
 * Creates a REST connection from an AI-generated proposal.
 * Backend handles all connection/action creation logic.
 * @param {Object} proposal - The proposal data from action.data
 * @returns {Promise<{connectionId: string, connectionName: string, actionsCreated: number, actionIds: string[]}>}
 */
// ==========================================
// Vector DB Linked Apps endpoints
// ==========================================

function unwrapAIResponse(response) {
  // Format 1: { RESULT: { ERROR, TOKEN, RESPONSE } } (uppercase - Integration style)
  const r = response.RESULT || response.result;
  if (r) {
    const token = r.TOKEN || r.token;
    const error = r.ERROR || r.error;
    const data = r.RESPONSE || r.response;
    if (token) sessionStorage.setItem('loginToken', token);
    const code = error ? (error.CODE || error.code) : 0;
    if (code && code !== 0 && code !== "0") {
      throw new Error(error.MESSAGE || error.message || 'Unknown error');
    }
    return data;
  }
  // Format 2: { error, response } (direct, no wrapper)
  if (response.error && response.error.code !== 0) {
    throw new Error(response.error.message);
  }
  return response.response || response;
}

export async function getLinkedApps(connectionId) {
  const token = sessionStorage.getItem('loginToken');
  const response = await makeRequest(getAIUrl('GetLinkedApps'), 'POST', { token, connectionId });
  return unwrapAIResponse(response);
}

export async function linkAppToVectorDB(connectionId, formId, formGUID, formName) {
  const token = sessionStorage.getItem('loginToken');
  const response = await makeRequest(getAIUrl('LinkAppToVectorDB'), 'POST', { token, connectionId, formId, formGUID, formName });
  return unwrapAIResponse(response);
}

export async function unlinkAppFromVectorDB(connectionId, formId) {
  const token = sessionStorage.getItem('loginToken');
  const response = await makeRequest(getAIUrl('UnlinkAppFromVectorDB'), 'POST', { token, connectionId, formId });
  return unwrapAIResponse(response);
}

export async function bulkSyncAppToVectorDb(connectionId, formId) {
  const token = sessionStorage.getItem('loginToken');
  const response = await makeRequest(getAIUrl('BulkSyncAppToVectorDb'), 'POST', { token, connectionId, formId });
  return unwrapAIResponse(response);
}

export async function getSyncJobStatus(jobId) {
  const response = await makeRequest(getAIUrl('GetSyncJobStatus'), 'POST', { jobId });
  return unwrapAIResponse(response);
}

export async function upsertFromAction(connectionId, fields) {
  const token = sessionStorage.getItem('loginToken');
  const response = await makeRequest(getAIUrl('UpsertFromAction'), 'POST', { token, connectionId, fields });
  return unwrapAIResponse(response);
}

export async function deleteFromAction(connectionId, instanceId) {
  const token = sessionStorage.getItem('loginToken');
  const response = await makeRequest(getAIUrl('DeleteFromAction'), 'POST', { token, connectionId, instanceId });
  return unwrapAIResponse(response);
}

export async function deleteVectorDbIndex(connectionId) {
  const token = sessionStorage.getItem('loginToken');
  const response = await makeRequest(getAIUrl('DeleteVectorDbIndex'), 'POST', { token, connectionId });
  return unwrapAIResponse(response);
}

export async function createConnectionFromProposal(proposal) {
  const token = sessionStorage.getItem('loginToken');

  const requestBody = {
    token: token,
    proposal: proposal
  };

  try {
    const response = await makeRequest(getAIUrl('CreateConnectionFromProposal'), 'POST', requestBody);

    // Handle both wrapped (RESULT) and unwrapped response formats
    const result = response.RESULT || response;

    if (result.TOKEN) {
      sessionStorage.setItem('loginToken', result.TOKEN);
    }

    if (result.ERROR && result.ERROR.CODE !== 0 && result.ERROR.CODE !== "0") {
      console.error('API Error:', result.ERROR.MESSAGE);
      throw new Error(result.ERROR.MESSAGE);
    }

    return result.RESPONSE;
  } catch (error) {
    console.error('Error during CreateConnectionFromProposal:', error);
    throw error;
  }
}
